import { apiFetch } from '../api/client';
import { dataViaApi } from '../api/mode';
import { GraphError, graphFetch } from './client';
import { getSiteId } from './site';
import { executeListOps, withoutClientKey, type ListOp } from './listOps';
import { namesClientKey } from '../data/clientKey';
import { cachedRead, invalidateList } from './readCache';

/**
 * Typed SharePoint list-item access. Every filterable column referenced here
 * is indexed by the provisioning script; do not add $filter clauses on
 * non-indexed columns (they fail once a list passes the 5,000-item view
 * threshold).
 */

export interface SpListItem<TFields> {
  id: string;
  fields: TFields & { id: string };
  createdDateTime: string;
  lastModifiedDateTime: string;
  /** Changes on every update; send back as If-Match to refuse stale writes (412). */
  eTag?: string;
  /** Browser-openable URL — set for document-library items (files). */
  webUrl?: string;
}

interface ListItemsPage<TFields> {
  value: SpListItem<TFields>[];
  '@odata.nextLink'?: string;
}

export interface QueryOptions {
  /** OData $filter over fields/... — indexed columns only. */
  filter?: string;
  orderBy?: string;
  /**
   * MAXIMUM rows returned (also the page size). Omit to fetch every page.
   * With orderBy, this is "the first N in that order" — paging stops once
   * N rows are in hand instead of downloading the rest of the list.
   */
  top?: number;
  /**
   * Only these columns (G5: list pages don't need multi-KB JSON columns).
   * Rows still carry `id`; leave out to get every column.
   */
  select?: readonly string[];
  /**
   * Reuse this exact query's result for this long (ms) in this tab
   * (readCache.ts — small, often-read lists only). Writes to the list from
   * this tab clear it.
   */
  cacheMs?: number;
}

export async function getListItems<TFields>(
  listName: string,
  options: QueryOptions = {},
): Promise<SpListItem<TFields>[]> {
  if (options.cacheMs) {
    const { cacheMs, ...query } = options;
    return cachedRead(listName, JSON.stringify(query), cacheMs, () => fetchListItems<TFields>(listName, query));
  }
  return fetchListItems<TFields>(listName, options);
}

async function fetchListItems<TFields>(
  listName: string,
  options: QueryOptions,
): Promise<SpListItem<TFields>[]> {
  if (dataViaApi()) {
    // Same query, answered by the server with only the rows this user may
    // see (api/src/policy.ts). `top` keeps its meaning: the first N rows.
    const res = await apiFetch<{ value: SpListItem<TFields>[]; truncated?: boolean }>(
      `/lists/${encodeURIComponent(listName)}/query`,
      {
        method: 'POST',
        body: { filter: options.filter, orderBy: options.orderBy, top: options.top, select: options.select },
      },
    );
    if (res.truncated) {
      // A guard rail stopped the server's scan early (api/src/listQuery.ts).
      console.warn(`${listName}: server returned only the first ${res.value.length} rows it found`);
    }
    return res.value;
  }
  const siteId = await getSiteId();
  const params = new URLSearchParams();
  params.set('expand', options.select ? `fields(select=${options.select.join(',')})` : 'fields');
  if (options.filter) params.set('$filter', options.filter);
  if (options.orderBy) params.set('$orderby', options.orderBy);
  params.set('$top', String(options.top ?? 200));

  let url: string | undefined =
    `/sites/${siteId}/lists/${encodeURIComponent(listName)}/items?${params.toString()}`;

  const items: SpListItem<TFields>[] = [];
  while (url) {
    const page: ListItemsPage<TFields> = await graphFetch<
      ListItemsPage<TFields>
    >(url);
    items.push(...page.value);
    url = page['@odata.nextLink'];
    if (options.top !== undefined && items.length >= options.top) break;
  }
  return options.top !== undefined ? items.slice(0, options.top) : items;
}

/** Fetch a single item by id; returns null on 404. */
export async function getListItem<TFields>(
  listName: string,
  itemId: number | string,
): Promise<SpListItem<TFields> | null> {
  if (dataViaApi()) {
    try {
      return await apiFetch<SpListItem<TFields>>(
        `/lists/${encodeURIComponent(listName)}/items/${encodeURIComponent(String(itemId))}`,
      );
    } catch (err) {
      // The server answers 404 for rows this user may not see, too.
      if (err instanceof GraphError && err.status === 404) return null;
      throw err;
    }
  }
  const siteId = await getSiteId();
  try {
    return await graphFetch<SpListItem<TFields>>(
      `/sites/${siteId}/lists/${encodeURIComponent(listName)}/items/${itemId}?expand=fields`,
    );
  } catch (err) {
    if (err instanceof GraphError && err.status === 404) return null;
    throw err;
  }
}

export async function createListItem<TFields>(
  listName: string,
  fields: Partial<TFields>,
): Promise<SpListItem<TFields>> {
  invalidateList(listName);
  try {
    if (dataViaApi()) {
      return (await writeOne({ method: 'POST', list: listName, fields })) as SpListItem<TFields>;
    }
    const siteId = await getSiteId();
    const post = (body: Partial<TFields>) =>
      graphFetch<SpListItem<TFields>>(`/sites/${siteId}/lists/${encodeURIComponent(listName)}/items`, {
        method: 'POST',
        body: { fields: body },
      });
    try {
      return await post(fields);
    } catch (err) {
      // Before provisioning adds ClientKey (src/data/clientKey.ts): create without it.
      const keyed = (fields as Record<string, unknown>).ClientKey !== undefined;
      if (!(keyed && err instanceof GraphError && err.status === 400 && namesClientKey(err.message))) throw err;
      return await post(withoutClientKey(fields as Record<string, unknown>) as Partial<TFields>);
    }
  } finally {
    invalidateList(listName); // a read made DURING the write must not stick
  }
}

/** Optional concurrency guard: the item's eTag as last read (Graph answers 412 if it changed since). */
export interface WriteOptions {
  ifMatch?: string;
}

const matchHeader = (options: WriteOptions) =>
  options.ifMatch ? { headers: { 'If-Match': options.ifMatch } } : {};

export async function updateListItem<TFields>(
  listName: string,
  itemId: string,
  fields: Partial<TFields>,
  options: WriteOptions = {},
): Promise<void> {
  invalidateList(listName);
  try {
    if (dataViaApi()) {
      await writeOne({ method: 'PATCH', list: listName, itemId, fields, ...ifMatchOf(options) });
      return;
    }
    const siteId = await getSiteId();
    await graphFetch(
      `/sites/${siteId}/lists/${encodeURIComponent(listName)}/items/${itemId}/fields`,
      { method: 'PATCH', body: fields, ...matchHeader(options) },
    );
  } finally {
    invalidateList(listName);
  }
}

export async function deleteListItem(
  listName: string,
  itemId: string,
  options: WriteOptions = {},
): Promise<void> {
  invalidateList(listName);
  try {
    if (dataViaApi()) {
      await writeOne({ method: 'DELETE', list: listName, itemId, ...ifMatchOf(options) });
      return;
    }
    const siteId = await getSiteId();
    await graphFetch(
      `/sites/${siteId}/lists/${encodeURIComponent(listName)}/items/${itemId}`,
      { method: 'DELETE', ...matchHeader(options) },
    );
  } finally {
    invalidateList(listName);
  }
}

const ifMatchOf = (options: WriteOptions) => (options.ifMatch ? { ifMatch: options.ifMatch } : {});

/**
 * One write through the server (/api/write — the same endpoint batches
 * use). A refused or failed op comes back as an error result; here it
 * throws the GraphError the direct path would (412 stays 412, etc.).
 */
async function writeOne(op: Omit<ListOp, 'id'>): Promise<unknown> {
  const res = (await executeListOps([{ id: 'w', ...op }])).get('w');
  if (res && res.status >= 200 && res.status < 300) return res.body;
  const err = (res?.body as { error?: { code?: string; message?: string } } | undefined)?.error;
  throw new GraphError(res?.status ?? 500, err?.code ?? String(res?.status ?? 500), err?.message ?? 'Write failed.');
}

/** Escape a string literal for use inside an OData $filter expression. */
export function odataString(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}
